/**
 * Slice 58 — numbers behind the one-page job report and the Reports list.
 * Everything counts as Here unless it is marked, or a cleaner's flag on that item is still open
 * (then it is pre-marked Broken). The flag and the check can never disagree: both read the same item.
 */
import { readSimpleFlowState, type SimpleFlowState } from "@/lib/simpleTurnover";

export type ItemStatus = "here" | "missing" | "broken";

export type FlagLike = {
  id: string;
  unit_item_id?: string | null;
  status: string | null;
  checklist_name?: string | null;
  created_at?: string | null;
  photos?: string[] | null;
  notes?: string | null;
  task_description?: string | null;
  room_name?: string | null;
};

export type MarkLike = { unit_item_id: string; status: ItemStatus; issue_report_id?: string | null; note?: string | null };

/** Reports the owner's own unit check created. They are marks, not flags raised by the cleaner. */
export const OWNER_CHECK_LIST_NAME = "Unit check";

export function isCleanerFlag(r: Pick<FlagLike, "checklist_name">): boolean {
  return r.checklist_name !== OWNER_CHECK_LIST_NAME;
}

export function isFlagOpen(r: Pick<FlagLike, "status">): boolean {
  return (r.status ?? "open") !== "resolved";
}

/** Latest open cleaner flag on each item. */
export function openFlagByItem(flags: FlagLike[]): Map<string, FlagLike> {
  const map = new Map<string, FlagLike>();
  for (const f of flags) {
    if (!f.unit_item_id || !isCleanerFlag(f) || !isFlagOpen(f)) continue;
    map.set(f.unit_item_id, f);
  }
  return map;
}

export function itemStatus(itemId: string, marks: MarkLike[], flagByItem: Map<string, FlagLike>): ItemStatus {
  const mark = marks.find((m) => m.unit_item_id === itemId);
  if (mark) return mark.status;
  return flagByItem.has(itemId) ? "broken" : "here";
}

export type MaintenanceSummary = { total: number; done: number; missed: string[]; complete: boolean };

export function maintenanceSummary(stateRaw: unknown): MaintenanceSummary {
  const state: SimpleFlowState = readSimpleFlowState(stateRaw);
  const rows = Object.values(state.maintenance_checks ?? {});
  const done = rows.filter((r) => r.status === "done").length;
  const missed = rows.filter((r) => r.status !== "done").map((r) => r.title?.trim() || "Maintenance");
  return { total: rows.length, done, missed, complete: rows.length === 0 || done === rows.length };
}

export type LowRow = { name: string; level: string; empty: boolean };

type SubmittedItem = { item_id?: string; item_name?: string; count?: number; fullness_value?: number };
type InvItemLike = {
  id: string;
  item_name?: string | null;
  quantity_type?: string | null;
  threshold_count?: number | null;
  threshold_value?: number | null;
};

const LEVELS: Record<number, string> = { 1: "Full", 0.75: "3/4 full", 0.5: "Half full", 0.25: "1/4 full", 0: "Empty" };

/** Items the Inv step reported low or empty. Matched to the clean by time, since a submission has no job id. */
export function lowFromSubmission(items: unknown, inventory: InvItemLike[]): LowRow[] {
  if (!Array.isArray(items)) return [];
  const byId = new Map(inventory.map((i) => [i.id, i]));
  const out: LowRow[] = [];
  for (const raw of items as SubmittedItem[]) {
    const inv = raw.item_id ? byId.get(raw.item_id) : undefined;
    const name = (raw.item_name || inv?.item_name || "").trim();
    if (!name) continue;
    const fullness = (inv?.quantity_type ?? "units").toLowerCase() === "fullness" || raw.fullness_value != null;
    if (fullness) {
      const v = raw.fullness_value;
      if (v == null) continue;
      const threshold = inv?.threshold_value ?? 0.25;
      if (v === 0) out.push({ name, level: "Reported empty in the Inv step", empty: true });
      else if (v <= threshold) out.push({ name, level: `${LEVELS[v] ?? `${Math.round(v * 100)}%`} left`, empty: false });
    } else {
      const c = raw.count;
      if (c == null) continue;
      const threshold = inv?.threshold_count ?? null;
      if (c === 0) out.push({ name, level: "Reported empty in the Inv step", empty: true });
      else if (threshold != null && c <= threshold) {
        out.push({ name, level: `${c} left · low at ${threshold}`, empty: false });
      }
    }
  }
  return out;
}

export type SubmissionRow = { cleaner_id: string | null; submitted_at: string; items: unknown };

/** The inventory submission made during this clean (started → finished, plus a short grace period). */
export function submissionForTask(
  subs: SubmissionRow[],
  task: { started_at?: string | null; completed_at?: string | null; cleaner_id?: string | null },
): SubmissionRow | null {
  if (!task.started_at) return null;
  const from = new Date(task.started_at).getTime();
  const to = (task.completed_at ? new Date(task.completed_at).getTime() : Date.now()) + 60 * 60 * 1000;
  const hits = subs
    .filter((s) => {
      const t = new Date(s.submitted_at).getTime();
      return t >= from - 60_000 && t <= to && (!task.cleaner_id || !s.cleaner_id || s.cleaner_id === task.cleaner_id);
    })
    .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime());
  return hits[0] ?? null;
}

export type ReportCounts = {
  missing: number;
  broken: number;
  flags: number;
  maintenance: MaintenanceSummary;
  low: number;
  clear: boolean;
};

export function reportCounts(input: {
  itemIds: string[];
  marks: MarkLike[];
  flags: FlagLike[];
  entryWalkthroughState: unknown;
  lowCount: number;
}): ReportCounts {
  const flagByItem = openFlagByItem(input.flags);
  let missing = 0;
  let broken = 0;
  for (const id of input.itemIds) {
    const s = itemStatus(id, input.marks, flagByItem);
    if (s === "missing") missing += 1;
    else if (s === "broken") broken += 1;
  }
  const flags = input.flags.filter((f) => isCleanerFlag(f) && isFlagOpen(f)).length;
  const maintenance = maintenanceSummary(input.entryWalkthroughState);
  // Low inventory never flips a job to Review on its own.
  const clear = missing === 0 && broken === 0 && flags === 0 && maintenance.complete;
  return { missing, broken, flags, maintenance, low: input.lowCount, clear };
}

export function verdictLine(c: ReportCounts, totalItems: number): string {
  if (c.clear) return "All clear.";
  const here = Math.max(0, totalItems - c.missing - c.broken);
  let line = `Unit check: ${here} here`;
  if (c.missing) line += `, ${c.missing} missing`;
  if (c.broken) line += `, ${c.broken} broken`;
  line += ". ";
  if (!c.maintenance.complete) {
    line += `Maintenance: checks not completed (${c.maintenance.done} of ${c.maintenance.total}).`;
  } else {
    line += `Maintenance: ${c.flags ? `${c.flags} flag${c.flags === 1 ? "" : "s"}.` : "no flags."}`;
  }
  return line;
}
